import React, { useEffect, useState } from 'react';
import {
    Box,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    makeStyles,
} from '@material-ui/core';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import { getStaffResumen } from '../../services/staff-lector';

const useStyles = makeStyles(theme => ({
    centeredState: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 180,
    },
    sectionLabel: {
        fontSize: 11,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.6px',
        color: theme.palette.text.section,
        marginBottom: theme.spacing(1.5),
        marginTop: theme.spacing(3),
        paddingBottom: theme.spacing(1),
        borderBottom: `1px solid ${theme.palette.border.section}`,
        '&:first-child': { marginTop: 0 },
        display: 'flex',
        alignItems: 'center',
        gap: 6,
    },
    helpIcon: {
        fontSize: 14,
        color: theme.palette.text.disabled,
        cursor: 'default',
    },
    statStrip: {
        display: 'flex',
        flexWrap: 'wrap',
        border: `1px solid ${theme.palette.border.soft}`,
        borderRadius: 5,
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.palette.shadow.card,
        overflow: 'hidden',
    },
    statItem: {
        flex: '1 1 auto',
        padding: theme.spacing(1.75, 2.5),
        borderRight: `1px solid ${theme.palette.border.soft}`,
        minWidth: 100,
        '&:last-child': { borderRight: 'none' },
    },
    statLabel: {
        fontSize: 11,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(0.5),
        whiteSpace: 'nowrap',
    },
    statValue: {
        fontSize: 20,
        fontWeight: 700,
        color: theme.palette.primary.main,
        lineHeight: 1.2,
    },
    statCaption: {
        fontSize: 11,
        color: theme.palette.text.disabled,
        marginTop: 2,
    },
    // table — exact match to BillsList.js
    listContainer: {
        borderRadius: 5,
        border: `1px solid ${theme.palette.border.soft}`,
        boxShadow: theme.palette.shadow.card,
        overflow: 'hidden',
        backgroundColor: theme.palette.common.white,
    },
    tableHead: {
        backgroundColor: theme.palette.surface.panel,
    },
    headCell: {
        color: theme.palette.text.section,
        fontWeight: 700,
        borderBottom: `2px solid ${theme.palette.border.main}`,
        whiteSpace: 'nowrap',
        fontSize: 12,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    bodyRow: {
        '&:nth-child(even)': {
            backgroundColor: theme.palette.surface.soft,
        },
    },
    bodyCell: {
        borderBottom: `1px solid ${theme.palette.border.subtle}`,
        color: theme.palette.text.secondary,
        whiteSpace: 'nowrap',
    },
    fieldName: {
        fontWeight: 700,
        color: theme.palette.grey.main,
    },
    badgeHigh: {
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: 4,
        fontSize: 12,
        fontWeight: 600,
        backgroundColor: theme.palette.status.successSoft,
        color: theme.palette.status.success,
        border: `1px solid ${theme.palette.status.success}22`,
    },
    badgeMid: {
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: 4,
        fontSize: 12,
        fontWeight: 600,
        backgroundColor: theme.palette.warning.light,
        color: theme.palette.warning.dark,
        border: `1px solid ${theme.palette.warning.main}33`,
    },
    badgeLow: {
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: 4,
        fontSize: 12,
        fontWeight: 600,
        backgroundColor: theme.palette.error.light,
        color: theme.palette.error.dark,
        border: `1px solid ${theme.palette.error.main}22`,
    },
}));

function StatStrip({ stats }) {
    const classes = useStyles();
    return (
        <Box className={classes.statStrip}>
            {stats.map(stat => (
                <Box key={stat.label} className={classes.statItem}>
                    <Typography className={classes.statLabel}>{stat.label}</Typography>
                    <Typography className={classes.statValue}>{stat.value ?? '—'}</Typography>
                    {stat.caption && (
                        <Typography className={classes.statCaption}>{stat.caption}</Typography>
                    )}
                </Box>
            ))}
        </Box>
    );
}

function ProblemBadge({ pct }) {
    const classes = useStyles();
    if (pct == null) return <span>—</span>;
    if (pct < 30) return <span className={classes.badgeHigh}>{pct.toFixed(1)}%</span>;
    if (pct < 60) return <span className={classes.badgeMid}>{pct.toFixed(1)}%</span>;
    return <span className={classes.badgeLow}>{pct.toFixed(1)}%</span>;
}

function fmt(n, decimals = 0) {
    if (n == null) return '—';
    return Number(n).toLocaleString('es-AR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

export default function ResumenTab() {
    const classes = useStyles();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        getStaffResumen()
            .then(res => setData(res.data))
            .catch(() => setError('No se pudo cargar el resumen del sistema.'))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <Box className={classes.centeredState}><CircularProgress size={32} /></Box>;
    if (error) return <Typography color="error">{error}</Typography>;
    if (!data) return null;

    const { procesamiento, scores, huerfanas, actividad, clientes_activos, costos, campos_problematicos } = data;

    return (
        <Box>
            <Typography className={classes.sectionLabel}>Procesamiento</Typography>
            <StatStrip stats={[
                { label: 'Total',           value: fmt(procesamiento.total) },
                { label: 'OK',              value: fmt(procesamiento.ok) },
                { label: 'Error',           value: fmt(procesamiento.error) },
                { label: 'Procesando',      value: fmt(procesamiento.processing) },
                { label: 'Abortado',        value: fmt(procesamiento.abortado) },
                { label: 'Tasa de éxito',   value: `${fmt(procesamiento.tasa_exito_pct, 1)}%` },
                { label: 'Duración prom.',  value: `${fmt(procesamiento.duracion_promedio_segundos, 1)}s` },
            ]} />

            <Typography className={classes.sectionLabel}>Scores y calidad</Typography>
            <StatStrip stats={[
                { label: 'Con score',       value: fmt(scores.total_facturas) },
                { label: 'Score promedio',  value: fmt(scores.promedio_sistema, 1) },
                { label: 'Alto (≥80)',      value: fmt(scores.distribucion?.alto) },
                { label: 'Medio (50–79)',   value: fmt(scores.distribucion?.medio) },
                { label: 'Bajo (<50)',      value: fmt(scores.distribucion?.bajo) },
                { label: 'Clientes activos', value: fmt(clientes_activos) },
            ]} />

            <Typography className={classes.sectionLabel}>Actividad y huérfanas</Typography>
            <StatStrip stats={[
                { label: 'Hoy',         value: fmt(actividad.hoy) },
                { label: 'Semana',      value: fmt(actividad.semana) },
                { label: 'Mes',         value: fmt(actividad.mes) },
                { label: 'YTD',         value: fmt(actividad.ytd) },
                { label: 'Año anterior', value: fmt(actividad.anio_anterior) },
                { label: 'Huérfanas',   value: fmt(huerfanas.total), caption: `${fmt(huerfanas.pct, 1)}% del total` },
            ]} />

            <Typography className={classes.sectionLabel}>Costos IA</Typography>
            <StatStrip stats={[
                { label: 'Scans totales',     value: fmt(costos.n_scans) },
                { label: 'Con LLM',           value: fmt(costos.n_scans_con_llm) },
                { label: 'Costo total',       value: `$${fmt(costos.total_cost_usd, 2)}`, caption: 'USD' },
                { label: 'Costo prom./scan',  value: costos.avg_cost_per_scan_usd != null ? `$${fmt(costos.avg_cost_per_scan_usd, 4)}` : '—', caption: 'USD' },
                { label: 'Duración prom.',    value: costos.avg_duration_seconds != null ? `${fmt(costos.avg_duration_seconds, 1)}s` : '—' },
                { label: 'Tokens input',      value: costos.total_tokens_input != null ? fmt(costos.total_tokens_input) : '—' },
            ]} />

            <Typography className={classes.sectionLabel}>
                Campos problemáticos
                <Tooltip
                    title="Muestra los 10 campos con mayor indicador de problema entre las últimas 2000 facturas procesadas. El % de problema refleja la proporción de lecturas con score medio o bajo para ese campo."
                    arrow
                    placement="right"
                >
                    <HelpOutlineIcon  style={{ color: '#2586bc' }} className={classes.helpIcon} />
                </Tooltip>
            </Typography>
            <TableContainer className={classes.listContainer}>
                <Table size="small">
                    <TableHead className={classes.tableHead}>
                        <TableRow>
                            <TableCell className={classes.headCell}>Campo</TableCell>
                            <TableCell className={classes.headCell} align="right">Total</TableCell>
                            <TableCell className={classes.headCell} align="right">Alto</TableCell>
                            <TableCell className={classes.headCell} align="right">Medio</TableCell>
                            <TableCell className={classes.headCell} align="right">Bajo</TableCell>
                            <TableCell className={classes.headCell} align="right">% Problema</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(campos_problematicos || []).map(campo => (
                            <TableRow key={campo.field} className={classes.bodyRow}>
                                <TableCell className={classes.bodyCell}>
                                    <span className={classes.fieldName}>{campo.label}</span>
                                </TableCell>
                                <TableCell className={classes.bodyCell} align="right">{fmt(campo.total)}</TableCell>
                                <TableCell className={classes.bodyCell} align="right">{fmt(campo.alto)}</TableCell>
                                <TableCell className={classes.bodyCell} align="right">{fmt(campo.medio)}</TableCell>
                                <TableCell className={classes.bodyCell} align="right">{fmt(campo.bajo)}</TableCell>
                                <TableCell className={classes.bodyCell} align="right">
                                    <ProblemBadge pct={campo.pct_problema} />
                                </TableCell>
                            </TableRow>
                        ))}
                        {(!campos_problematicos || campos_problematicos.length === 0) && (
                            <TableRow>
                                <TableCell colSpan={6} align="center" style={{ padding: 24 }}>
                                    <Typography variant="body2" color="textSecondary">Sin datos</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}
